import { AlertCircle } from "lucide-react";
import type { SandboxAuditMessage, SandboxAuditThreadDetail, SandboxAuditThreadSummary } from "@/api/sandboxAudit";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { SandboxMarkdown } from "@/components/sandbox/SandboxMarkdown";
import { formatAuditTime, modelLabel } from "./format";

type Props = {
  thread: SandboxAuditThreadSummary | null;
  detail: SandboxAuditThreadDetail | null;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
};

// Only the visible exchange belongs in the transcript: system/tool rows and
// assistant placeholders still waiting on a reply carry no readable content.
export function transcriptMessages(messages: SandboxAuditMessage[]) {
  return messages.filter(
    (message) => (message.role === "user" || message.role === "assistant") && Boolean(message.content?.trim()),
  );
}

export function AuditThreadSheet({ thread, detail, isLoading, error, onClose }: Props) {
  const header = detail ?? thread;
  const messages = detail ? transcriptMessages(detail.messages) : [];
  const author = header?.user_email ?? "Unknown person";

  return (
    <Sheet open={thread !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-b px-6 py-4 pr-12 text-left">
          <SheetTitle className="break-words">{header?.title ?? "Thread"}</SheetTitle>
          <SheetDescription>
            {header ? `${author} · ${modelLabel(header)} · ${formatAuditTime(header.created_at)}` : null}
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5" data-testid="audit-transcript">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {isLoading && !error && (
            <div className="space-y-4">
              <Skeleton className="ml-auto h-12 w-2/3" />
              <Skeleton className="h-28 w-full" />
            </div>
          )}
          {!isLoading && !error && detail && messages.length === 0 && (
            <p className="text-sm text-muted-foreground">This thread has no messages yet.</p>
          )}
          {!isLoading && !error && messages.length > 0 && (
            <ol className="space-y-6">
              {messages.map((message) => (
                <TranscriptEntry key={message.id} message={message} author={author} />
              ))}
            </ol>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TranscriptEntry({ message, author }: { message: SandboxAuditMessage; author: string }) {
  const time = formatAuditTime(message.created_at);

  if (message.role === "user") {
    return (
      <li aria-label={`Prompt from ${author}`} className="flex flex-col items-end gap-1">
        <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-primary/10 px-4 py-2.5 text-sm">
          {message.content}
        </div>
        <span className="text-xs text-muted-foreground">{time}</span>
      </li>
    );
  }

  return (
    <li aria-label="Response" className="flex flex-col gap-1">
      <SandboxMarkdown content={message.content ?? ""} />
      <span className="text-xs text-muted-foreground">
        {time}
        {message.error_code ? ` · Response failed (${message.error_code})` : ""}
      </span>
    </li>
  );
}
